import React, { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";

import useDonationSound from "../hooks/useDonationSound";
import {
  animateDonationFeedShift,
  getDonationFeedPositionMap,
  playDonationArrivalCelebration,
} from "../utils/donationAnimation";

function formatAmount(value) {
  return `$${Number(value || 0).toLocaleString("en-US")}`;
}

function itemClassName(item, index, activeBatchId) {
  const classes = [];

  const isInitialLatest = !activeBatchId && index === 0;
  const isActive = item.batchId && item.batchId === activeBatchId;

  if (isInitialLatest || isActive) {
    classes.push("donation-card--active");
  } else if (activeBatchId) {
    classes.push("donation-card--dimmed");
  }

  if (item.isNew) {
    classes.push(
      item.kind === "group"
        ? "donation-card--inserted"
        : "donation-card--new-arrival",
    );
  }

  return classes.join(" ") || undefined;
}

const DonationFeed = ({ feedItems, activeBatchId }) => {
  const listRef = useRef(null);
  const layerRef = useRef(null);
  const positionsRef = useRef(null);
  const measuredForRef = useRef(null);
  const playSound = useDonationSound();

  /*
    Capture pre-render geometry so the layout effect can FLIP the cards
    that moved. Measuring forces layout, so only do it on the render
    that actually changes the feed — the dashboard re-renders on every
    websocket message, and the feed can be long.
  */
  if (measuredForRef.current !== feedItems) {
    measuredForRef.current = feedItems;
    positionsRef.current = getDonationFeedPositionMap(listRef.current);
  }

  useLayoutEffect(() => {
    animateDonationFeedShift(listRef.current, positionsRef.current);
  }, [feedItems]);

  useEffect(() => {
    if (!activeBatchId) return undefined;

    /*
      Sound first, and unconditionally: it must not depend on finding
      the card in the DOM. Only the confetti needs a real element to
      anchor to, so a missed lookup costs the burst, never the sound.

      Keyed by batch so this effect re-running for a batch we already
      played — a remount when the dashboard toggles back from invites —
      stays silent. The confetti below is free to replay.
    */
    playSound(activeBatchId);

    const card = listRef.current?.querySelector(
      `[data-batch-id="${activeBatchId}"]`,
    );

    if (!card) {
      console.log("DONATION CARD NOT FOUND FOR BATCH:", activeBatchId);
      return undefined;
    }

    return playDonationArrivalCelebration(layerRef.current, card);
  }, [activeBatchId, playSound]);

  return (
    <>
      <ul
        ref={listRef}
        className="donation-feed"
        aria-live="polite"
        aria-relevant="additions"
      >
        {feedItems.map((item, index) => (
          <li
            key={item.feedId}
            className={itemClassName(item, index, activeBatchId)}
            data-feed-id={item.feedId}
            data-batch-id={item.batchId || undefined}
          >
            <section>
              <div>
                {item.kind === "group" ? (
                  <>
                    <span>
                      <b>{`${item.count} donations`}</b>
                      <br />
                      Arrived together
                    </span>
                    <strong>{formatAmount(item.total)}</strong>
                  </>
                ) : (
                  <>
                    <span>
                      <b>
                        {`${item.sale.customer?.firstName || ""} ${
                          item.sale.customer?.lastName || ""
                        }`}
                      </b>
                      <br />
                      Donated to{" "}
                      <p
                        style={{
                          display: "inline",
                          textTransform: "capitalize",
                        }}
                      >
                        {`${item.sale.userFirstName} ${item.sale.userLastName}`}
                      </p>
                    </span>
                    <strong>{formatAmount(item.sale.paymentAmount)}</strong>
                  </>
                )}
              </div>
            </section>
          </li>
        ))}
      </ul>

      {/*
        Portalled to <body> so no ancestor can clip or restack it. The
        feed container scrolls (overflow-y: scroll) and the bursts are
        positioned in viewport coordinates, so the layer must not live
        inside it. Matches the prototype, where the layer is a
        top-level sibling of .layout-content.
      */}
      {createPortal(
        <div
          ref={layerRef}
          id="donationAnimationLayer"
          className="donation-animation-layer"
          aria-hidden="true"
        />,
        document.body,
      )}
    </>
  );
};

export default React.memo(DonationFeed);
